import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import Container from 'components/Container';
import { Trans, useTranslation } from 'services/localization';
import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { useParams } from 'react-router';
import { Redirect } from 'react-router-dom';
import history from 'services/history';
import {
	Plan,
	PlanDefinitions,
	Plans,
	isSubscriptionPlan,
	SubscriptionPlan,
	getUsedBlockSlots,
	isPlanDowngrade,
	getPreviousPlanName,
	calculateUpcomingSubscriptionDate,
	PurchasedPlan,
	getDefaultPurchasedPlan,
	getNewSubscription,
} from 'common/modules/plans';
import { ById } from 'common/types';
import { push, Routes, useTypedLocation } from 'routing';
import PurchaseConfirmationCard, {
	ContentString,
	LineItem,
} from 'components/PurchaseConfirmationCard';
import PlanValueProp from './components/PlanValueProp';
import { Callable } from 'common/frontend/callable';
import { getPlanKeyFeatures } from 'services/plans/utils';
import Banner from 'components/Banner';
import { RiExternalLinkLine } from 'react-icons/ri';
import moment from 'moment-timezone';
import { getPricingString } from 'common/utils/common';
import { BILLING_CURRENCY } from 'common/modules/billing';
import { shouldChargeVat } from 'common/modules/billing/utils/vat';
import { getPurchaseSubscriptionPriceBreakdown } from 'common/modules/plans/subscriptions';
import { getShopLocations } from 'common/utils/shopUtils';
import { chain } from 'lodash';
import * as IntercomService from 'services/intercom';
import { logEvent, newPlanChangeEvent } from 'common/services/analytics';
import ChurnModal from './components/ChurnModal';
import { RENTLE_FREE_LANGUAGES } from 'common/locales/utils/constants';
import { GOOGLE_CONVERSION_VALUE } from 'common/services/analytics/constants';

const ConfirmPlan = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { planId } = useParams<{ planId: SubscriptionPlan | 'LITE' }>();
	const plan = Plans[planId];
	const [loading, setLoading] = useState<boolean>(false);
	const [showChurnModal, setShowChurnModal] = useState<boolean>(false);
	const shopPlan = useSelector(ShopSelectors.shopPlan);
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const shopLanguages = useSelector(ShopSelectors.activeShopLanguages);
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly)!;
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const locationSubscriptions = shopReadOnly.features.plan.subscription?.locations;
	const hasSamePlan = plan === shopPlan;
	const hasCustomBilling = shopReadOnly.billing.billingMethod === 'custom';

	const upcomingBillingDateInPast = !!locationSubscriptions
		? chain(locationSubscriptions)
				.values()
				.minBy('upcomingBillingDate')
				.thru(({ upcomingBillingDate }) => moment().isAfter(moment(upcomingBillingDate)))
				.value()
		: false;

	const [
		revenueDiscountEligibility,
		setRevenueDiscountEligibility,
	] = useState<ById<boolean> | null>(null);
	const [
		revenueDiscountEligibilityLoading,
		setRevenueDiscountEligibilityLoading,
	] = useState<boolean>(true);

	const chargeVat = shouldChargeVat(shopPublicInfo.country, shopReadOnly.billing.vatNumber);
	const nextBillingDate = shopReadOnly.features.plan.subscription?.upcomingBillingDate
		? moment(shopReadOnly.features.plan.subscription.upcomingBillingDate)
		: calculateUpcomingSubscriptionDate(moment(), 1);

	const shouldRedirect = useRef<boolean>(
		!plan || hasSamePlan || (plan !== 'LITE' && !isSubscriptionPlan(plan)),
	);
	const { state } = useTypedLocation();

	const planDefinition = PlanDefinitions[planId];
	const usedBlockSlots = getUsedBlockSlots(shopFeatures);
	const planBlockSlots = planDefinition.blockSlots;
	const isOverBlockSlotLimit = usedBlockSlots > planBlockSlots;
	const isOverLanguageLimit = planId === 'LITE' && shopLanguages.length > 1;
	const shopLanguageIsNotFreeLanguage =
		planId === 'LITE' && shopLanguages.some((lang) => !RENTLE_FREE_LANGUAGES.includes(lang));

	const isDowngrade = isPlanDowngrade(plan, shopPlan);

	useEffect(() => {
		const loadRevenueDiscountEligibility = async (merchantId: string) => {
			setRevenueDiscountEligibilityLoading(true);
			const data = await Callable.billing.getRevenueDiscountEligibilityByLocation({
				merchantId,
			});
			setRevenueDiscountEligibility(data);
			setRevenueDiscountEligibilityLoading(false);
		};

		loadRevenueDiscountEligibility(shopId);
	}, [shopId]);

	const handleContactSupportClick = () => {
		IntercomService.open();
		logEvent('chat_with_us_press', { from: history.location.pathname });
	};

	const handlePlanChangeAnalytics = () => {
		IntercomService.trackEvent(IntercomService.PLAN_CHANGED, {
			from: shopPlan,
			to: plan,
			type: isDowngrade ? 'downgrade' : 'upgrade',
		});
		newPlanChangeEvent({
			fromPlan: shopPlan!,
			toPlan: plan,
			type: isDowngrade ? 'downgrade' : 'upgrade',
			...(!isDowngrade && { currency: 'EUR', value: GOOGLE_CONVERSION_VALUE }), // see https://docs.google.com/document/d/1__jTGvfZdB4jtngrJeOTr_lpJWTr1-yE0t-Lt_ODr-8/edit?usp=sharing for more info
		});
	};

	const confirmationCardContent: ContentString[] = (() => {
		const locationIds = getShopLocations(shopPublicInfo).map((l) => l.id);

		const content = [
			{
				title: t('account.stores.newPlan', 'New plan'),
				content: planDefinition.name,
			},
		];

		const isFreePlan = (planDefinition.pricing?.fixed?.amount.value ?? 0) === 0;

		if (!isFreePlan) {
			content.push({
				title: t('common:payment.payPer', 'Pay per'),
				content: t('common:times.monthPerStore', 'Month / Store'),
			});
		}

		const price = planDefinition.pricing?.fixed?.amount.value ?? 0;

		content.push({
			title: t('common:payment.price'),
			content: !!price
				? getPricingString(price, BILLING_CURRENCY)
				: t('common:billing.free', 'Free'),
		});

		content.push({
			title: t('stores.pageTitle', 'Stores'),
			content: locationIds.length + '',
		});

		return content;
	})();

	const lineItems: LineItem[] = useMemo(() => {
		const now = moment.utc();
		const newPlan: PurchasedPlan = getDefaultPurchasedPlan(plan, now);
		const locationIds = getShopLocations(shopPublicInfo).map((l) => l.id);

		if (upcomingBillingDateInPast || hasCustomBilling) {
			return [];
		}

		if (isSubscriptionPlan(plan)) {
			const currentSubscription = shopReadOnly.features.plan.subscription;
			newPlan.subscription = getNewSubscription({
				startDate: now,
				locationIds,
				subscriptionDates: !!currentSubscription
					? {
							previousBillingDate: currentSubscription.previousBillingDate,
							upcomingBillingDate: currentSubscription.upcomingBillingDate,
					  }
					: undefined,
			});
		}

		const priceBreakdown = getPurchaseSubscriptionPriceBreakdown({
			shopPublicInfo,
			shopReadOnly,
			newPlan,
			discountEligibilityByLocation: revenueDiscountEligibility ?? {},
			date: now,
		});

		return [
			{
				label: t('plans.billingBreakdown.subscriptionPrice', 'Subscription price'),
				amount: priceBreakdown.totalSubscriptionPrice.value,
			},
			{
				label: t('plans.billingBreakdown.revenueDiscount', 'Revenue discount'),
				amount: -1 * priceBreakdown.totalRevenueDiscount.value,
				loading: revenueDiscountEligibilityLoading,
			},
			{
				label: t('plans.billingBreakdown.planChangeCredit', 'Plan change discount'),
				amount:
					-1 * (priceBreakdown.totalStartDiscount.value + priceBreakdown.totalCancelRefund.value),
			},
			{
				label: t('plans.billingBreakdown.creditsUsed', 'Credits used'),
				amount: -1 * priceBreakdown.totalCreditsToUse.total,
			},
		];
	}, [
		plan,
		shopPublicInfo,
		upcomingBillingDateInPast,
		hasCustomBilling,
		shopReadOnly,
		revenueDiscountEligibility,
		t,
		revenueDiscountEligibilityLoading,
	]);

	if (shouldRedirect.current) {
		return <Redirect to={Routes.PLANS} />;
	}

	const handleConfirm = async (plan: Plan) => {
		setLoading(true);
		try {
			await Callable.plans.subscriptions.purchase({
				merchantId: shopId,
				plan,
			});
			setLoading(false);

			dispatch(
				NotificationActions.showNotification({
					variant: 'info',
					message: t('plans.notification.updated', 'Plan updated'),
				}),
			);
			handlePlanChangeAnalytics();
			push(isDowngrade ? Routes.ACCOUNT_BILLING : state?.from ?? '');
		} catch (err) {
			setLoading(false);
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('plans.notification.failed', 'Plan update failed. Please try again.'),
				}),
			);
		}
	};

	return (
		<Container>
			<Stack direction="column" spacing={2}>
				{isOverBlockSlotLimit && (
					<Banner
						title={t('plans.downgrade.tooManyBlocks', 'Too many blocks installed')}
						description={t('plans.downgrade.pleaseUninstall', {
							amount: usedBlockSlots - planBlockSlots,
							defaultValue:
								'Please uninstall {{amount}} block(s) to be able to change to this plan.',
						})}
						variant="warning"
						button={{
							text: t('plans.downgrade.manageBlocks', 'Manage blocks'),
							onClick: () => push(Routes.BlockStoreTab('installed')),
						}}
					/>
				)}
				{isOverLanguageLimit && (
					<Banner
						title={t('plans.downgrade.tooManyLanguages', 'Too many languages active')}
						description={t(
							'plans.downgrade.removeLanguages',
							'Multiple languages are not included in this plan. Please make sure you have only one active language to be able to change to this plan.',
						)}
						variant="warning"
						button={{
							text: t('plans.downgrade.editLanguages', 'Edit languages'),
							onClick: () => push(Routes.LanguagesTab('settings')),
						}}
					/>
				)}
				{!isOverLanguageLimit && shopLanguageIsNotFreeLanguage && (
					<Banner
						title={t('plans.downgrade.mainLanguageNotIncluded', 'Main language not included')}
						description={t(
							'plans.downgrade.changeMainLanguage',
							'Your main language is not included in this plan. Please make sure your main language is available to be able to change to this plan.',
						)}
						variant="warning"
						button={{
							text: t('plans.downgrade.editLanguages', 'Edit languages'),
							onClick: () => push(Routes.LanguagesTab('settings')),
						}}
					/>
				)}
				{upcomingBillingDateInPast && !hasCustomBilling && (
					<Banner
						title={t('plans.change.errorTitle', 'Cannot change plan')}
						description={t(
							'plans.change.errorDescription',
							'There are issues with your current subscription which block a plan change. Please contact support.',
						)}
						variant="warning"
						button={{
							text: t('plans.change.errorBtn', 'Contact support'),
							onClick: handleContactSupportClick,
						}}
					/>
				)}
				{hasCustomBilling && (
					<Banner
						title={t('plans.change.customBillingTitle', 'Cannot change plan')}
						description={t(
							'plans.change.customBillingDescription',
							'Your account has agreed on a custom deal with Rentle. To change plan, please contact support.',
						)}
						variant="warning"
						button={{
							text: t('plans.change.errorBtn', 'Contact support'),
							onClick: handleContactSupportClick,
						}}
					/>
				)}
			</Stack>

			<Grid
				container
				spacing={2}
				justifyContent="center"
				sx={{
					paddingTop: (theme) => theme.spacing(4),
				}}
			>
				<Grid
					item
					xs={12}
					md={7}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: {
							xs: 'center',
							md: 'stretch',
						},
					}}
				>
					<Typography variant="h4" textAlign={{ xs: 'center', md: 'left' }} sx={{ mb: 2 }}>
						{planDefinition.name}
					</Typography>
					{plan !== 'LITE' && (
						<Typography variant="body1" textAlign={{ xs: 'center', md: 'left' }} sx={{ mb: 2 }}>
							<Trans
								i18nKey="plans.valueProps.header"
								defaults="This plan includes everything in <planlink>{{previousPlan}}</planlink>, and:"
								values={{ previousPlan: getPreviousPlanName(plan) }}
								components={{
									planlink: (
										<Link
											href="https://rentle.io/pricing"
											target="_blank"
											rel="noopener noreferrer"
										>
											{'Rentle Grow'}
										</Link>
									),
								}}
							/>
						</Typography>
					)}

					<Box sx={{ mt: 4 }}>
						{getPlanKeyFeatures(plan, t)
							.filter((f) => !f.hideInConfirm)
							.map((feature) => (
								<PlanValueProp key={feature.title} {...feature} />
							))}
					</Box>
					<Link
						sx={{
							textDecoration: 'none',
						}}
						href="https://rentle.io/pricing"
						rel="noopener noreferrer"
						target="_blank"
					>
						<Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							{t('common:actions.learnMore', 'Learn more')}
							<Box display="inline-block" sx={{ ml: 0.5 }}>
								<RiExternalLinkLine size={16} />
							</Box>
						</Typography>
					</Link>
				</Grid>
				<Grid
					item
					xs={12}
					md={5}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: {
							xs: 'center',
							md: 'stretch',
						},
					}}
				>
					<PurchaseConfirmationCard
						header={t('plans.purchasePlanTitle', 'Purchase plan')}
						cardContent={confirmationCardContent}
						lineItems={lineItems}
						cta={{
							label: t('plans.purchasePlanCTA', 'Start plan'),
							loading,
							onClick: () => (plan === 'LITE' ? setShowChurnModal(true) : handleConfirm(plan)),
						}}
						isFree={plan === 'LITE'}
						disabled={
							isOverBlockSlotLimit ||
							isOverLanguageLimit ||
							shopLanguageIsNotFreeLanguage ||
							upcomingBillingDateInPast ||
							hasSamePlan ||
							hasCustomBilling
						}
						currency={BILLING_CURRENCY}
						chargeVat={chargeVat}
						nextBillingDate={upcomingBillingDateInPast ? undefined : nextBillingDate}
					/>
				</Grid>
			</Grid>
			{showChurnModal && (
				<ChurnModal
					isOpen={showChurnModal}
					onClose={() => setShowChurnModal(false)}
					onConfirm={() => handleConfirm(plan)}
				/>
			)}
		</Container>
	);
};

export default ConfirmPlan;
